// dependencies
import React, { useEffect, useMemo, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer } from "../../../components/Admin";
// components
import Button from "../../../components/Button";
import PageBanner from "../../../components/PageBanner";
import Pagination from "../../../components/Pagination";
import Spacer from "../../../components/Spacer";
import TableLoading from "../../../components/TableLite/components/TableLoading";
import Typography from "../../../components/Text";
import Search from "./components/SearchTeams";
import TeamsIntegrationTable from "./components/TeamsTable";

import { useFetchTeamsConfig, useFetchTeamsList, useFetchTeamsMetadata } from "../../../api/hooks";
// redux
// hooks
import useRequest from "../../../hooks/useRequest";

import get from "../../../utils/GetNested";

import propTypes from "../../../lib/prop-types";

// styles
import css from "./Teams.module.scss";
// selectors
// utils
import getSearchResults from "./search";

const PAGE_SIZE = 20;

const TeamsFeedbackSettings = (props) => {
	const { hasAdminConsent, hasAdminConsentV2 } = props.adminConsent;
	const { showLoadingState, teamsIntegrationFeatureEnabled } = props;

	// state data objects
	const [teamsSettings, setTeamsSettings] = useState({});
	const [currentPageOfTeams, setCurrentPageOfTeams] = useState([]);
	const [sortedTeamsList, setSortedTeamsList] = useState([]);
	const [installStatuses, setInstallStatuses] = useState({});

	// state single values
	const [jobInProgress, setJobInProgress] = useState(false);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [tenantID, setTenantID] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");

	// requests
	const { data: teamsConfigData, refetch: getTeamsConfig } = useFetchTeamsConfig();

	const { data: teamsMetadata, isFetching: requestingTeamsMetadata } = useFetchTeamsMetadata();

	const { data: teamsList, isFetching: requestingTeamsList, refetch: getTeamsList } = useFetchTeamsList();

	const {
		data: installStatusData,
		isRequesting: requestingTeamsInstallStatus,
		revalidate: getInstallStatus,
	} = useRequest("getTeamsInstallStatus", {}, { skipCache: false });

	// select data from state
	const showTableLoading = requestingTeamsMetadata || requestingTeamsList || jobInProgress || showLoadingState;
	const selectPageCount = (totalItems) => Math.ceil(totalItems / PAGE_SIZE);

	// helpers & handlers
	const revalidateData = () => {
		getTeamsConfig();
		getTeamsList();
		const currentPageIDs = currentPageOfTeams.map((team) => team.id);
		getInstallStatus({ filters: { teamIDs: currentPageIDs } });
	};

	const handleSearch = (newSearch) => {
		if (newSearch) {
			setSearchTerm(newSearch);
		} else {
			setSearchTerm("");
		}
	};

	const sortTeamsList = (listOfTeams) => {
		const sorted = listOfTeams.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));

		return sorted;
	};

	const getCurrentPageOfTeams = (listOfTeams, pageNumber) => {
		const skip = (pageNumber - 1) * PAGE_SIZE;
		const currentPage = listOfTeams.slice(skip, PAGE_SIZE + skip);

		setCurrentPageOfTeams(currentPage);

		return currentPage;
	};

	const handlePaginationClick = (pageNumber) => {
		setPage(pageNumber);
		getCurrentPageOfTeams(sortedTeamsList, pageNumber);
	};

	// handle updates
	useMemo(() => {
		if (teamsMetadata) {
			setTenantID(get(teamsMetadata, ["tenantID"], null));
		}
	}, [teamsMetadata]);

	// new data coming in
	useEffect(() => {
		if (teamsConfigData) {
			setTeamsSettings(get(teamsConfigData, ["data", "settings"], null));
		}
	}, [teamsConfigData]);

	useEffect(() => {
		if (teamsList && teamsList.teams) {
			// sort the teams list
			const sorted = sortTeamsList(teamsList.teams);
			setSortedTeamsList(sorted);
		}
	}, [teamsList]);

	// changes to installStatusData
	useEffect(() => {
		if (installStatusData) {
			setInstallStatuses({ ...installStatuses, ...installStatusData });
		}
	}, [installStatusData]);

	// changes to sorted data
	useEffect(() => {
		if (sortedTeamsList) {
			// set the page count
			setTotalPages(selectPageCount(sortedTeamsList.length));

			// set the current page
			const currentPage = getCurrentPageOfTeams(sortedTeamsList, page);
			setCurrentPageOfTeams(currentPage);
		}
	}, [sortedTeamsList]);

	// changes to the current page of data
	useEffect(() => {
		if (currentPageOfTeams) {
			// The most basic of cacheing to avoid refetching on every
			// page change.
			const currentPageIDs = currentPageOfTeams.map((team) => team.id);
			const alreadyFetchedInstallStatuses = Object.keys(installStatuses);
			const alreadyFetched = currentPageIDs.every((id) => alreadyFetchedInstallStatuses.includes(id));

			if (!alreadyFetched || currentPageIDs.length === 0) {
				getInstallStatus({ filters: { teamIDs: currentPageIDs } });
			}
		}
	}, [currentPageOfTeams]);

	useMemo(() => {
		if (searchTerm && get(teamsList, ["teams"], []).length > 0) {
			const searchResults = getSearchResults(searchTerm, ["displayName"], teamsList.teams);
			setSortedTeamsList(searchResults);
			setPage(1);
		} else if (searchTerm === "" && get(teamsList, ["teams"], []).length > 0) {
			setSortedTeamsList(teamsList.teams);
			setPage(1);
		}
	}, [searchTerm]);

	if (!teamsIntegrationFeatureEnabled && !showLoadingState) {
		return (
			<AdminContainer>
				<div className={css.teamsContainer}>
					<div className={css.infoBox}>
						<div className={css.infoIcon}>
							<FontAwesomeIcon icon={faInfoCircle} />
						</div>
						<div>This feature is not enabled for your organization.</div>
					</div>
				</div>
			</AdminContainer>
		);
	}

	if (hasAdminConsent === false && hasAdminConsentV2 === false && !showLoadingState) {
		return (
			<AdminContainer>
				<PageBanner
					size="large"
					type={"info"}
					isDismissible={false}
					title="To enable this feature for MS Teams, please authorize the PerformYard App by clicking the button
					below"
					description="Please note that you must be a Microsoft Teams Administrator to complete this step"
					actionButtonText="Begin Authorization"
					actionButtonHref="/microsoft_teams/get-admin-consent/?"
				/>
			</AdminContainer>
		);
	}

	const requestingTeamsDataResponse = (
		<div className={css.loadingTable}>
			<TableLoading cols={3} />
		</div>
	);

	return (
		<div>
			{!showTableLoading ? (
				<div>
					<Search updateSearch={handleSearch} />
					<Spacer height={2} />
					<div className={css.teamsTableContainer}>
						<TeamsIntegrationTable
							teamsList={currentPageOfTeams}
							tenantID={tenantID}
							teamsSettings={teamsSettings}
							installStatus={installStatuses}
							requestingInstallStatus={requestingTeamsInstallStatus}
							revalidateData={() => revalidateData()}
							setJobInProgress={setJobInProgress}
						/>
					</div>
				</div>
			) : (
				requestingTeamsDataResponse
			)}
			<div className={css.teamsPagination}>
				<Pagination currentPage={page} pageCount={totalPages} onClick={handlePaginationClick} />
			</div>
		</div>
	);
};
TeamsFeedbackSettings.propTypes = {
	adminConsent: propTypes.object.isRequired,
	teamsIntegrationFeatureEnabled: propTypes.bool.isRequired,
	showLoadingState: propTypes.bool.isRequired,
};

export default TeamsFeedbackSettings;
